import React, { useState, useEffect } from "react";
import {
  Card,
  Container,
  Typography,
  TextField,
  Box,
  Button,
  Stack,
  Grid,
  CircularProgress
} from "@mui/material";
import Page from "../components/Page";
import sidebarConfig from "../layouts/dashboard/SidebarConfig";
import { addUpdatePageInstructionApi, getPageInstructionApi } from "src/api/commonapi";
import { notification } from "src/utils/messages";
import CustomLoader from "src/commonComponents/CustomLoader";
import normalizeSearch from "src/utils/normalizeSearch";

export default function RoutesView() {
  const [routeData, setRouteData] = useState({});
  const [allRoutes, setAllRoutes] = useState([]);
  const [newRouteDataList, setNewRouteDataList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [filtereddata, setFilteredData] = useState({
    search: ""
  });

  useEffect(() => {
    generateRoutesList();
  }, []);

  const generateRoutesList = () => {
    const routesData = [];

    // Function to extract routes from sidebar config
    const extractRoutes = (config, category) => {
      config.forEach((item) => {
        if (item && item.path && item.path !== "#" && item.path !== "/location") {
          routesData.push({
            path: item.path.replace("/", ""),
            name: item.title,
            category: category
          });
        }

        // Handle children routes
        if (item && item.children && Array.isArray(item.children)) {
          item.children.forEach((child) => {
            if (child.path && child.path !== "#") {
              routesData.push({
                path: child.path.replace("/", ""),
                name: `${child.title} (${item.title})`,
                category: category
              });
            }
          });
        }
      });
    };

    // Extract routes from different user types
    let loginUser = JSON.parse(localStorage.getItem("userData"));

    if (
      loginUser?.u_type == 0 ||
      (loginUser?.u_type == 2 && loginUser?.p_user_id == 1 && loginUser?.management_p != 0)
    ) {
      // Admin routes
      extractRoutes(sidebarConfig, "Admin");
    } else if (loginUser?.u_type == 1 || (loginUser?.u_type == 2 && loginUser?.management_p != 0)) {
      // City Ambassador routes
      extractRoutes(sidebarConfig, "City Ambassador");
    } else {
      // Staff routes
      extractRoutes(sidebarConfig, "Staff");
    }

    // Remove duplicates based on path
    const uniqueRoutes = routesData.filter(
      (route, index, self) => index === self.findIndex((r) => r.path === route.path)
    );

    setAllRoutes(uniqueRoutes);
  };

  const getPageInstruction = async () => {
    setIsLoading(true);
    const response = await getPageInstructionApi();
    setIsLoading(false);
    let data = response?.data || {};
    let cleanedPageData = [];
    if (Array.isArray(data.page_data)) {
      cleanedPageData = [...data.page_data];
    }
    Object.keys(data).forEach((key) => {
      if (!isNaN(key)) {
        cleanedPageData.push(data[key]);
      }
    });
    setRouteData({ page_data: cleanedPageData });
  };

  const handleDataChange = (routePath, field, value, name) => {
    setRouteData((prevData) => {
      const existingPageData = prevData.page_data || [];

      const existingIndex = existingPageData.findIndex((item) => item.page_route === routePath);

      let updatedPageData;
      if (existingIndex >= 0) {
        updatedPageData = [...existingPageData];
        updatedPageData[existingIndex] = {
          ...updatedPageData[existingIndex],
          page_route: routePath,
          page_name: name, // or use route.name
          [field]: value
        };
      } else {
        // Add new route entry
        updatedPageData = [
          ...existingPageData,
          {
            page_route: routePath,
            page_name: name, // or use route.name
            [field]: value
          }
        ];
      }

      return { ...prevData, page_data: updatedPageData };
    });
  };

  const handleSaveAllData = async () => {
    setIsLoading(true);

    await addUpdatePageInstructionApi(routeData).then((response) => {
      let notify = notification({
        type: response.status == 0 ? "error" : "success",
        message: response.message,
        position: "top"
      });
      notify();
      getPageInstruction();
    });
    setIsLoading(false);
  };

  useEffect(() => {
    getPageInstruction();
  }, []);

  const filterData = () => {
    const filterBySearchType = (route) => {
      let searchFilterText = filtereddata.search.toLowerCase();
      return filtereddata.search == "" || route?.name?.toLowerCase().includes(normalizeSearch(searchFilterText));
    };

    const nROUTE = allRoutes.filter((route) => filterBySearchType(route));

    setNewRouteDataList(nROUTE);
  };

  useEffect(() => {
    filterData();
  }, [filtereddata, allRoutes]);

  return (
    <Page title="Routes Management | Local Vocal Business Group">
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="h4" gutterBottom>
            Routes Management
          </Typography>
          <Stack direction="row" spacing={2}>
            <TextField
              size="small"
              label="Search Route"
              value={filtereddata?.search}
              onChange={(e) => setFilteredData({ ...filtereddata, search: e.target.value })}
            />
            <Button
              variant="outlined"
              onClick={() => handleSaveAllData()}
              disabled={isLoading}
              startIcon={isLoading && <CircularProgress size={20} color="inherit" />}
            >
              Save
            </Button>
          </Stack>
        </Stack>

        <Card
          sx={{
            height: "calc(100vh - 220px)",
            overflow: "hidden",
            borderRadius: "12px 0px 0px 12px"
          }}
        >
          <Box
            sx={{
              p: 3,
              height: "100%",
              overflowY: "auto"
            }}
          >
            <Grid container spacing={3}>
              {isLoading ? (
                <CustomLoader />
              ) : (
                newRouteDataList.map((route, ind) => (
                  <Grid item xs={12} md={12} lg={12} key={route.path}>
                    <Card variant="outlined" sx={{ p: 2, height: "100%" }}>
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            {`${ind + 1}. `} {route.name}
                          </Typography>
                          {/* <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Path: {route.path}
                        </Typography>
                        <Chip 
                          label={route.category} 
                          color={getCategoryColor(route.category)}
                          size="small"
                        /> */}
                        </Box>

                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          placeholder="Add notes for this route..."
                          value={
                            routeData.page_data?.find((item) => item.page_route === route.path)
                              ?.page_des || ""
                          }
                          onChange={(e) =>
                            handleDataChange(route.path, "page_des", e.target.value, route.name)
                          }
                          variant="outlined"
                          size="small"
                        />
                      </Stack>
                      <Stack spacing={2} sx={{ pt: 2 }}>
                        <Grid container>
                          <Grid item xs={12} sm={6} sx={{ pr: 1 }}>
                            <TextField
                              fullWidth
                              placeholder="URL"
                              value={
                                routeData.page_data?.find((item) => item.page_route === route.path)
                                  ?.button_url || ""
                              }
                              onChange={(e) =>
                                handleDataChange(
                                  route.path,
                                  "button_url",
                                  e.target.value,
                                  route.name
                                )
                              }
                              variant="outlined"
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              placeholder="Button Title"
                              value={
                                routeData.page_data?.find((item) => item.page_route === route.path)
                                  ?.btn_title || ""
                              }
                              onChange={(e) =>
                                handleDataChange(
                                  route.path,
                                  "btn_title",
                                  e.target.value,
                                  route.name
                                )
                              }
                              variant="outlined"
                              size="small"
                            />
                          </Grid>
                        </Grid>
                      </Stack>
                    </Card>
                  </Grid>
                ))
              )}
            </Grid>

            {newRouteDataList.length === 0 && (
              <Box textAlign="center" py={4}>
                <Typography color="text.secondary">
                  {isLoading ? (
                    <CustomLoader />
                  ) : (
                    "No routes found. Please check the route configuration."
                  )}
                </Typography>
              </Box>
            )}
          </Box>
        </Card>
      </Container>
    </Page>
  );
}
